import { ArrowLeftIcon } from 'lucide-react';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';

import { Skeleton, Text } from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

const BOTTOM_BAR_HEIGHT = 52;
const SCROLL_THRESHOLD = 50;
const TOP_BAR_HEIGHT = 64;
const HYSTERESIS = 40;
const COOLDOWN_MS = 300;

interface Props {
  children: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  backButton?: { text: string; href: string; onClick?: (e: React.MouseEvent) => void };
  isLoading?: boolean;
  actions?: React.ReactNode;
  headerActions?: React.ReactNode;
}

export const FormPageTemplate = ({
  children,
  title,
  subtitle,
  backButton,
  isLoading,
  actions,
  headerActions,
}: Props) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const cooldownRef = useRef(false);
  const stateRef = useRef(false);

  useEffect(() => {
    stateRef.current = window.scrollY > SCROLL_THRESHOLD;
    setIsScrolled(stateRef.current);

    const handleScroll = () => {
      if (cooldownRef.current) return;

      const scrollY = window.scrollY;
      let shouldChange = false;
      let newState = stateRef.current;

      if (stateRef.current) {
        if (scrollY < SCROLL_THRESHOLD - HYSTERESIS) {
          newState = false;
          shouldChange = true;
        }
      } else {
        if (scrollY > SCROLL_THRESHOLD + HYSTERESIS) {
          newState = true;
          shouldChange = true;
        }
      }

      if (shouldChange) {
        stateRef.current = newState;
        setIsScrolled(newState);

        cooldownRef.current = true;
        setTimeout(() => {
          cooldownRef.current = false;
        }, COOLDOWN_MS);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header
        className={cn(
          'tw:sticky tw:z-[1099] tw:bg-background tw:transition-all tw:duration-200 tw:ease-in-out',
          isScrolled ? 'tw:py-2 tw:border-b tw:border-border' : 'tw:pb-4 tw:border-b-0 tw:border-transparent',
        )}
        style={{ top: TOP_BAR_HEIGHT }}
      >
        <div className="tw:container tw:max-w-[1440px] tw:mx-auto tw:px-6">
          <div
            className={cn(
              'tw:flex tw:transition-all tw:duration-200 tw:ease-in-out',
              isScrolled ? 'tw:flex-row tw:gap-4 tw:items-center' : 'tw:flex-col tw:gap-2 tw:items-start',
            )}
          >
            {backButton && (
              <Link
                href={backButton.href}
                onClick={backButton.onClick}
                className="tw:inline-flex tw:items-center tw:no-underline tw:text-inherit tw:hover:underline"
              >
                <ArrowLeftIcon
                  className={cn(
                    'tw:mr-2 tw:transition-all tw:duration-200 tw:ease-in-out',
                    isScrolled ? 'tw:w-4 tw:h-4' : 'tw:w-6 tw:h-6',
                  )}
                />
                {!isScrolled && (
                  <Text variant="body-md" weight="semibold">
                    {backButton.text}
                  </Text>
                )}
              </Link>
            )}

            {isLoading ? (
              <div className="tw:flex tw:flex-col tw:gap-1">
                <Skeleton variant="text" width={180} height={isScrolled ? 24 : 36} />
                {!isScrolled && <Skeleton variant="text" width={240} height={20} />}
              </div>
            ) : (
              <>
                {title && (
                  <div className="tw:flex tw:items-center tw:justify-between tw:w-full">
                    <Text
                      variant="display-sm"
                      as="h1"
                      className={cn(
                        'tw:m-0 tw:transition-all tw:duration-200 tw:ease-in-out',
                        isScrolled ? 'tw:text-[1.125rem]' : 'tw:text-[2.25rem]',
                      )}
                    >
                      {title}
                    </Text>
                    {headerActions && <div className="tw:flex tw:items-center">{headerActions}</div>}
                  </div>
                )}
                {!isScrolled && subtitle && (
                  <Text
                    variant="body-md"
                    color="muted"
                    className="tw:transition-opacity tw:duration-200 tw:ease-in-out tw:opacity-100"
                  >
                    {subtitle}
                  </Text>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      <main
        style={{
          paddingBottom: actions ? `${BOTTOM_BAR_HEIGHT + 48}px` : '2rem',
        }}
      >
        <div className="tw:container tw:max-w-[1440px] tw:mx-auto tw:px-6 tw:pt-2">
          <div className="tw:flex tw:flex-col tw:gap-6">{children}</div>
        </div>
      </main>

      {actions && (
        <div className="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:py-2 tw:border-t tw:border-border tw:bg-card tw:z-[1100] tw:lg:pl-[280px]">
          <div className="tw:container tw:max-w-[1440px] tw:mx-auto tw:px-6">
            <div className="tw:flex tw:flex-row tw:justify-between tw:items-center">{actions}</div>
          </div>
        </div>
      )}
    </>
  );
};
